import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  message,
  Row,
  Col,
  Card,
  Tooltip,
  Tag,
  DatePicker
} from 'antd';
// 移除路由相关导入
import { createSale, getAccounts, getAccount, updateSale } from '../services/api';

const { Option } = Select;
const { TextArea } = Input;

const SaleForm = ({ onSuccess, onCancel, accountId, saleId, sale, mode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  // 当传入的 sale 变化时，回填表单（initialValues 只在首次挂载生效）
  useEffect(() => {
    if (!sale) return;
    // 优先使用传入的 sale.account 立即展示，避免等待列表加载
    if (sale.account) {
      setSelectedAccount(sale.account);
      // 把该账号并入下拉选项，防止下拉里找不到
      setAccounts(prev => {
        const exists = prev.some(a => String(a.id) === String(sale.account.id || sale.account_id));
        if (exists) return prev;
        const merged = [...prev];
        merged.push({ ...(sale.account || {}), id: sale.account?.id || sale.account_id });
        return merged;
      });
    }

    form.setFieldsValue({
      status: sale.status || 'pending',
      paymentMethod: sale.paymentMethod || 'alipay',
      accountId: sale.account_id || accountId,
      buyerName: sale.buyerName,
      buyerContact: sale.buyerContact,
      salePrice: sale.salePrice != null ? sale.salePrice : sale.price,
      notes: sale.notes,
      saleDate: sale.created_at ? dayjs(sale.created_at) : sale.saleDate ? dayjs(sale.saleDate) : dayjs()
    });
  }, [sale, accountId, form]);

  useEffect(() => {
    if (!accountId) return;
    if (accounts.length > 0) {
      console.log('Setting account for sale:', accountId, accounts);
      const account = accounts.find(acc => String(acc.id) === String(accountId));
      if (account) {
        console.log('Found account:', account);
        setSelectedAccount(account);
        form.setFieldsValue({
          accountId: String(account.id),
          salePrice: account.price
        });
        return;
      }
    }
    // 若列表未找到，补拉单条
    (async () => {
      try {
        const res = await getAccount(accountId);
        const acc = res?.id ? res : res?.data || res; // 兼容不同返回结构
        if (acc && acc.id != null) {
          const merged = [...accounts];
          if (!merged.find(a => String(a.id) === String(acc.id))) merged.push(acc);
          setAccounts(merged);
          setSelectedAccount(acc);
          form.setFieldsValue({
            accountId: String(acc.id),
            salePrice: acc.price
          });
        } else {
          console.log('Account not found with ID:', accountId);
        }
      } catch (e) {
        console.log('Account not found with ID:', accountId, e?.message || e);
      }
    })();
  }, [accountId, accounts, form]);

  const fetchAccounts = async () => {
    try {
      const response = await getAccounts({ limit: 1000 });
      setAccounts(response.accounts);
    } catch (error) {
      message.error('获取账号列表失败');
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 计算利润
      // 统一字符串比较，避免数字/字符串混用导致未匹配
      const normalizedAccountId = String(values.accountId);
      const selectedAccount = accounts.find(acc => String(acc.id) === normalizedAccountId);
      if (selectedAccount) {
        values.profit = values.salePrice - (selectedAccount.cost || 0);
      }

      // 统一字段名称
      const saleDateValue = values.saleDate
        ? (typeof values.saleDate.toISOString === 'function'
            ? values.saleDate.toISOString()
            : dayjs(values.saleDate).toISOString())
        : new Date().toISOString();

      const saleData = {
        ...values,
        account_id: normalizedAccountId,
        price: values.salePrice,
        salePrice: values.salePrice,
        saleDate: saleDateValue,
        created_at: saleDateValue
      };
      delete saleData.accountId;
      delete saleData.saleDate;

      console.log('发送销售数据:', saleData);
      let result;
      if (mode === 'edit' && saleId) {
        result = await updateSale(saleId, saleData);
        message.success('销售记录已更新');
      } else {
        result = await createSale(saleData);
        message.success('销售记录创建成功');
      }
      if (onSuccess) {
        onSuccess(result || saleData);
      }
    } catch (error) {
      message.error('创建失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleAccountChange = (value) => {
    const account = accounts.find(acc => String(acc.id) === String(value));
    if (account) {
      setSelectedAccount(account);
      form.setFieldsValue({
        salePrice: account.price
      });
    } else {
      setSelectedAccount(null);
    }
  };


  return (
    <div>
      <Card title={mode === 'edit' ? '编辑销售记录' : '添加销售记录'} style={{ maxWidth: 800, margin: '0 auto' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: sale?.status || 'pending',
            paymentMethod: sale?.paymentMethod || 'alipay',
            accountId: sale?.account_id || accountId,
            buyerName: sale?.buyerName,
            buyerContact: sale?.buyerContact,
            salePrice: sale?.salePrice,
            notes: sale?.notes,
            saleDate: sale?.created_at ? dayjs(sale.created_at) : dayjs()
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="accountId"
                label="选择账号"
                rules={[{ required: true, message: '请选择要销售的账号' }]}
              >
                <Select
                  placeholder="搜索并选择账号"
                  onChange={handleAccountChange}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {accounts.map(account => (
                    <Option key={account.id} value={account.id}>
                      {account.username} (粉丝: {account.followers?.toLocaleString() || '0'}, 价格: ¥{account.price})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              {/* 显示选中的账号信息（分类直接读取账号管理里已设置的类型） */}
              {selectedAccount && (
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>已选择账号：</strong>
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {selectedAccount.username}
                    </Tag>
                    <Tag color={selectedAccount.category ? 'purple' : 'default'} style={{ marginLeft: 8 }}>
                      {selectedAccount.category || '未分类'}
                    </Tag>
                  </div>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Tooltip title="点击查看详细信息">
                        <div style={{ cursor: 'pointer' }}>
                          <strong>粉丝数：</strong>{selectedAccount.followers?.toLocaleString() || '0'}
                        </div>
                      </Tooltip>
                    </Col>
                    <Col span={8}>
                      <div>
                        <strong>分类：</strong>
                        {selectedAccount.category || '未分类'}
                        <span style={{ color: '#8c8c8c', fontSize: 12 }}>（来自账号）</span>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div><strong>状态：</strong>
                        <Tag color={selectedAccount.status === 'active' ? 'green' : 'orange'}>
                          {selectedAccount.status === 'active' ? '活跃' : 
                           selectedAccount.status === 'pending' ? '待售' : 
                           selectedAccount.status === 'sold' ? '已售' : selectedAccount.status}
                        </Tag>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={8}>
                      <div><strong>售价：</strong>¥{selectedAccount.price}</div>
                    </Col>
                    <Col span={8}>
                      <div><strong>成本：</strong>¥{selectedAccount.cost || 0}</div>
                    </Col>
                    <Col span={8}>
                      <div><strong>利润：</strong>¥{selectedAccount.profit || 0}</div>
                    </Col>
                  </Row>
                  {selectedAccount.description && (
                    <div style={{ marginTop: 8 }}>
                      <strong>描述：</strong>{selectedAccount.description}
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="buyerName"
                label="买家姓名"
                rules={[{ required: true, message: '请输入买家姓名' }]}
              >
                <Input placeholder="请输入买家姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="buyerContact"
                label="联系方式"
                rules={[{ required: true, message: '请输入联系方式' }]}
              >
                <Input placeholder="请输入手机号或微信号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="salePrice"
                label="销售价格"
                rules={[{ required: true, message: '请输入销售价格' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="销售价格"
                  min={0}
                  precision={2}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/¥\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentMethod"
                label="支付方式"
                rules={[{ required: true, message: '请选择支付方式' }]}
              >
                <Select placeholder="请选择支付方式">
                  <Option value="alipay">支付宝</Option>
                  <Option value="wechat">微信</Option>
                  <Option value="bank">银行转账</Option>
                  <Option value="cash">现金</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value="pending">待处理</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="cancelled">已取消</Option>
                  <Option value="refunded">已退款</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="saleDate"
                label="销售日期"
                rules={[{ required: true, message: '请选择销售日期' }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea
              rows={4}
              placeholder="请输入备注信息"
            />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {mode === 'edit' ? '更新销售记录' : '创建销售记录'}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default SaleForm;
